import { useRef, useState } from 'react';
import { useOpenIntakeBatchMutation, useUploadCardMutation, useExtractCardMutation } from './api.js';
import { Alert, Button, Card } from './ui.js';

/**
 * Several business cards, chosen at once.
 *
 * The camera flow stays what it is — one card, one photograph, reviewed carefully. This
 * is the other case: someone comes back from an event with a dozen photographs already
 * in their camera roll, and uploading them one at a time is the reason they will not
 * bother.
 *
 * Each card is uploaded independently into one intake batch. That is deliberate on two
 * counts. It needs no multipart parser on the server — the existing route takes raw image
 * bytes, and a header names the batch — and it means one unreadable photograph fails
 * alone. Nothing here is wrapped in an all-or-nothing anything.
 */

type ItemState = 'queued' | 'uploading' | 'reading' | 'ready' | 'duplicate' | 'failed';

interface Item {
  readonly key: string;
  readonly name: string;
  readonly previewUrl: string;
  state: ItemState;
  captureId: string | null;
  message: string | null;
}

const LABEL: Record<ItemState, string> = {
  queued: 'Waiting',
  uploading: 'Uploading',
  reading: 'Reading',
  ready: 'Ready to review',
  duplicate: 'Already added',
  failed: 'Failed',
};

const TONE: Record<ItemState, string> = {
  queued: 'bg-slate-100 text-slate-600',
  uploading: 'bg-sky-50 text-sky-800',
  reading: 'bg-sky-50 text-sky-800',
  ready: 'bg-emerald-50 text-emerald-800',
  duplicate: 'bg-amber-50 text-amber-800',
  failed: 'bg-red-50 text-red-800',
};

export function CardBatch({ onReviewItem, onViewInReview }: {
  onReviewItem?: (captureId: string) => void;
  /** Where the batch goes once read: Review, filtered to business cards. */
  onViewInReview?: () => void;
}) {
  const [openBatch] = useOpenIntakeBatchMutation();
  const [upload] = useUploadCardMutation();
  const [extract] = useExtractCardMutation();

  const [items, setItems] = useState<Item[]>([]);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [limit, setLimit] = useState<number | null>(null);
  const pickerRef = useRef<HTMLInputElement>(null);

  const patch = (key: string, next: Partial<Item>) => {
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, ...next } : i)));
  };

  const choose = (input: HTMLInputElement) => {
    const chosen = Array.from(input.files ?? []);
    input.value = '';
    if (chosen.length === 0) return;
    setError(null);
    setItems(chosen.map((f, i) => ({
      // Name plus size plus index: two photographs can share a name, and the key only
      // has to be stable within this selection.
      key: `${f.name}:${f.size}:${i}`,
      name: f.name,
      previewUrl: URL.createObjectURL(f),
      state: 'queued' as ItemState,
      captureId: null,
      message: null,
    })));
    setFiles(chosen);
  };

  const [files, setFiles] = useState<File[]>([]);

  const removeAt = (key: string) => {
    setItems((prev) => {
      const gone = prev.find((i) => i.key === key);
      if (gone) URL.revokeObjectURL(gone.previewUrl);
      return prev.filter((i) => i.key !== key);
    });
    setFiles((prev) => prev.filter((_, i) => items[i]?.key !== key));
  };

  const start = async () => {
    setRunning(true);
    setError(null);
    try {
      const { batch } = await openBatch({ source: 'business_card' }).unwrap();

      /*
       * One at a time, on purpose.
       *
       * Each card costs a paid extraction, and the upload endpoint is rate limited
       * precisely because it is expensive. Firing a dozen at once would meet that limit
       * and turn a working feature into a wall of 429s.
       */
      for (const [index, file] of files.entries()) {
        const item = items[index];
        if (!item) continue;
        patch(item.key, { state: 'uploading', message: null });
        try {
          const res = await upload({ file, batchId: batch.id }).unwrap();
          if (res.duplicate) {
            patch(item.key, { state: 'duplicate', message: 'This photo was already added.' });
            continue;
          }
          patch(item.key, { state: 'reading', captureId: res.capture.id });
          await extract(res.capture.id).unwrap();
          patch(item.key, { state: 'ready' });
        } catch (e) {
          // One card's failure is one card's failure. The loop continues.
          const data = (e as { data?: { message?: string; error?: string } }).data;
          if (data?.error === 'batch_full') {
            setLimit(files.length);
            patch(item.key, { state: 'failed', message: 'This intake is full.' });
            break;
          }
          patch(item.key, {
            state: 'failed',
            message: data?.message ?? 'This photo could not be read.',
          });
        }
      }
    } catch {
      setError('The upload could not be started. Try again.');
    } finally {
      setRunning(false);
    }
  };

  const done = items.filter((i) => i.state === 'ready').length;
  const failed = items.filter((i) => i.state === 'failed').length;
  const duplicates = items.filter((i) => i.state === 'duplicate').length;
  // Finished: every chosen photo has an answer, and at least one was read.
  const finished = !running && items.length > 0 && done > 0
    && items.every((i) => i.state === 'ready' || i.state === 'failed' || i.state === 'duplicate');

  return (
    <Card className="p-5">
      <h3 className="text-base font-semibold text-slate-900">Several cards at once</h3>
      <p className="mt-1 text-sm text-slate-500">
        Choose the business-card photos already on your phone. Each is read separately, so
        one unreadable photo does not affect the rest.
      </p>

      {/*
        `multiple` is the whole feature. The accept list stays the explicit three formats
        for the same reason the single picker does: Safari transcodes HEIC to JPEG on
        selection when HEIC is not named, which is what spares an iPhone user a rejection.
      */}
      <input
        ref={pickerRef}
        type="file"
        multiple
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => choose(e.currentTarget)}
      />

      <div className="mt-4 flex flex-wrap gap-2">
        <Button className="min-h-11" onClick={() => pickerRef.current?.click()} disabled={running}>
          Choose photos
        </Button>
        {items.length > 0 && !running ? (
          <Button className="min-h-11" variant="secondary" onClick={start} disabled={files.length === 0}>
            Read {items.length} card{items.length === 1 ? '' : 's'}
          </Button>
        ) : null}
      </div>

      {error ? <div className="mt-4"><Alert tone="error">{error}</Alert></div> : null}
      {limit !== null ? (
        <div className="mt-4">
          <Alert tone="info">
            An intake holds a limited number of cards. Read these, then start another batch
            for the rest.
          </Alert>
        </div>
      ) : null}

      {items.length > 0 ? (
        <>
          <p className="mt-4 text-sm text-slate-600">
            Selected: {items.length}
            {done > 0 ? ` · ${done} ready` : ''}
            {duplicates > 0 ? ` · ${duplicates} already added` : ''}
            {failed > 0 ? ` · ${failed} failed` : ''}
          </p>
          {finished && onViewInReview ? (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Button className="min-h-11" onClick={onViewInReview}>View in Review</Button>
              <span className="text-sm text-slate-600">Nothing is sent to your CRM until you approve it there.</span>
            </div>
          ) : null}
          <ul className="mt-3 space-y-2">
            {items.map((i) => (
              <li key={i.key} className="flex items-center gap-3 rounded-md border border-slate-200 p-2">
                <img
                  src={i.previewUrl}
                  alt=""
                  className="h-12 w-12 shrink-0 rounded object-cover"
                />
                <div className="min-w-0 flex-1">
                  {/* Truncated, never wrapped: a long filename must not push the status
                      badge off a phone screen. */}
                  <p className="truncate text-sm text-slate-800">{i.name}</p>
                  {i.message ? <p className="truncate text-xs text-slate-500">{i.message}</p> : null}
                </div>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${TONE[i.state]}`}>
                  {LABEL[i.state]}
                </span>
                {!running && i.state === 'queued' ? (
                  <button
                    type="button"
                    onClick={() => removeAt(i.key)}
                    aria-label={`Remove ${i.name}`}
                    className="shrink-0 px-1 text-slate-400"
                  >
                    ×
                  </button>
                ) : null}
                {i.state === 'ready' && i.captureId && onReviewItem ? (
                  <Button
                    variant="secondary"
                    className="shrink-0"
                    onClick={() => onReviewItem(i.captureId!)}
                  >
                    Review
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </Card>
  );
}
